import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Sparkles, Loader2, X, Check, AlertTriangle, Wand2,
  Minimize2, Maximize2, Briefcase, Baby, RefreshCw,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { useCredits } from "@/context/CreditsContext";
import { cvApi } from "@/lib/api";

const FIELD_TITLES = {
  summary: "Pomóż mi napisać — podsumowanie zawodowe",
  experience: "Pomóż mi napisać — opis doświadczenia",
};

// Akcje redakcyjne dostępne przy propozycji.
const REFINE_ACTIONS = [
  { key: "shorten", label: "Skróć", Icon: Minimize2 },
  { key: "expand", label: "Rozwiń", Icon: Maximize2 },
  { key: "professional", label: "Bardziej profesjonalnie", Icon: Briefcase },
  { key: "simpler", label: "Prostszym językiem", Icon: Baby },
  { key: "variant", label: "Inna wersja", Icon: RefreshCw },
];

// Pojedyncze pytanie: kafelki (single/multi) lub pole tekstowe; zawsze „Inne".
function QuestionCard({ q, value, other, onPick, onOther }) {
  const isMulti = q.type === "multi";
  const selected = isMulti ? (Array.isArray(value) ? value : []) : value;

  const toggle = (opt) => {
    if (isMulti) {
      const next = selected.includes(opt) ? selected.filter((x) => x !== opt) : [...selected, opt];
      onPick(next);
    } else {
      onPick(selected === opt ? "" : opt);
    }
  };

  return (
    <div data-testid={`cv-assist-q-${q.id}`}>
      <p className="mb-2 text-sm font-medium text-brand-text">{q.label}</p>
      {q.type === "text" ? (
        <input
          data-testid={`cv-assist-q-${q.id}-text`}
          className="w-full rounded-field border border-brand-line bg-brand-bg px-4 py-2.5 text-[15px] text-brand-text outline-none focus:border-brand-accent"
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onPick(e.target.value)}
          placeholder="Wpisz krótką odpowiedź"
        />
      ) : (
        <div className="flex flex-wrap gap-2">
          {q.options.map((opt) => {
            const on = isMulti ? selected.includes(opt) : selected === opt;
            return (
              <button
                key={opt}
                type="button"
                data-testid={`cv-assist-opt-${q.id}-${opt}`}
                onClick={() => toggle(opt)}
                className={`rounded-full border px-3.5 py-2 text-[13px] font-medium transition-colors ${
                  on ? "border-brand-accent bg-brand-accent/10 text-brand-text" : "border-brand-line text-brand-sec hover:text-brand-text"
                }`}
              >
                {opt}
              </button>
            );
          })}
          {q.allowOther && (
            <input
              data-testid={`cv-assist-other-${q.id}`}
              className="min-w-[140px] flex-1 rounded-full border border-brand-line bg-brand-bg px-3.5 py-2 text-[13px] text-brand-text outline-none focus:border-brand-accent"
              value={other || ""}
              onChange={(e) => onOther(e.target.value)}
              placeholder="Inne…"
            />
          )}
        </div>
      )}
    </div>
  );
}

export function CvWriteAssist({ open, onClose, cvId, field, expIndex = null, onInsert, onEvent }) {
  const { confirmAndRun } = useCredits();
  const [phase, setPhase] = useState("questions"); // questions | proposal
  const [questions, setQuestions] = useState([]);
  const [picks, setPicks] = useState({});
  const [others, setOthers] = useState({});
  const [loading, setLoading] = useState(false);
  const [proposal, setProposal] = useState("");
  const [error, setError] = useState("");
  const loadedRef = useRef(false);
  const generatedRef = useRef(false);
  const usedRefineRef = useRef(false);
  const insertedRef = useRef(false);

  const fire = (event) => onEvent && onEvent(event);

  useEffect(() => {
    if (!open) {
      loadedRef.current = false;
      generatedRef.current = false; usedRefineRef.current = false; insertedRef.current = false;
      setPhase("questions"); setQuestions([]); setPicks({}); setOthers({});
      setProposal(""); setError(""); setLoading(false);
      return;
    }
    if (loadedRef.current) return;
    loadedRef.current = true;
    (async () => {
      setLoading(true); setError("");
      try {
        const r = await cvApi.assist({ cv_id: cvId, field, phase: "questions", exp_index: expIndex });
        setQuestions(r.questions || []);
      } catch {
        setError("Nie udało się wczytać pytań. Spróbuj ponownie.");
      } finally {
        setLoading(false);
      }
    })();
  }, [open, cvId, field, expIndex]);

  // Łączy wybory z opcją „Inne" w jedną odpowiedź na pytanie.
  const buildAnswers = () => {
    const out = {};
    questions.forEach((q) => {
      const v = picks[q.id];
      const other = (others[q.id] || "").trim();
      if (q.type === "multi") {
        const arr = [...(Array.isArray(v) ? v : [])];
        if (other) arr.push(other);
        if (arr.length) out[q.id] = arr;
      } else {
        const val = other || (typeof v === "string" ? v : "");
        if (val) out[q.id] = val;
      }
    });
    return out;
  };

  const generate = async (action = "generate", baseText = "") => {
    setError("");
    const actionKey = action === "generate" ? "cv_write_assist" : "cv_write_assist_edit";
    const r = await confirmAndRun(actionKey, async () => {
      setLoading(true);
      try {
        return await cvApi.assist({
          cv_id: cvId, field, phase: "generate", action,
          answers: buildAnswers(), exp_index: expIndex, base_text: baseText,
        });
      } finally {
        setLoading(false);
      }
    });
    if (r && r.text !== undefined) {
      setProposal(r.text);
      setPhase("proposal");
      if (action === "generate") {
        if (!generatedRef.current) { generatedRef.current = true; fire("generated"); }
      } else {
        usedRefineRef.current = true;
      }
    }
  };

  // Zamknięcie bez wstawienia = odrzucenie (po wygenerowaniu) lub anulowanie (przed).
  const handleClose = () => {
    if (!insertedRef.current) fire(generatedRef.current ? "rejected" : "cancelled");
    onClose();
  };

  const handleInsert = () => {
    insertedRef.current = true;
    fire(usedRefineRef.current ? "accepted_after_variant" : "accepted_unchanged");
    onInsert(proposal);
    toast.success("Propozycja wstawiona. Możesz ją teraz edytować lub cofnąć.");
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent data-testid="cv-assist-modal" className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-brand-text">
            <Sparkles size={18} className="text-brand-accent" /> {FIELD_TITLES[field]}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Asystent redakcyjny CV — przygotowuje propozycję tekstu wyłącznie na podstawie danych podanych w kreatorze.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center gap-2 py-10 text-sm text-brand-sec" data-testid="cv-assist-loading">
            <Loader2 size={18} className="animate-spin text-brand-accent" /> Przygotowuję…
          </div>
        )}

        {!loading && phase === "questions" && (
          <div className="space-y-5" data-testid="cv-assist-questions">
            <p className="text-sm text-brand-sec">
              Skorzystam wyłącznie z informacji podanych w kreatorze. Odpowiedz na kilka szybkich pytań (opcjonalnie), a przygotuję propozycję.
            </p>
            {error && <p className="rounded-field border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300">{error}</p>}
            {questions.map((q) => (
              <QuestionCard
                key={q.id}
                q={q}
                value={picks[q.id]}
                other={others[q.id]}
                onPick={(v) => setPicks((p) => ({ ...p, [q.id]: v }))}
                onOther={(v) => setOthers((o) => ({ ...o, [q.id]: v }))}
              />
            ))}
            <div className="flex justify-end gap-2 border-t border-brand-line pt-4">
              <button type="button" data-testid="cv-assist-cancel" onClick={handleClose} className="rounded-button border border-brand-line px-4 py-2.5 text-sm font-semibold text-brand-text hover:border-brand-accent/60">
                Anuluj
              </button>
              <button type="button" data-testid="cv-assist-generate" onClick={() => generate("generate")} className="inline-flex items-center gap-2 rounded-button bg-brand-accent px-5 py-2.5 text-sm font-semibold text-brand-on transition-transform hover:-translate-y-0.5">
                <Wand2 size={15} /> Przygotuj propozycję
              </button>
            </div>
          </div>
        )}

        {!loading && phase === "proposal" && (
          <div className="space-y-4" data-testid="cv-assist-proposal">
            <div className="flex items-start gap-2 rounded-field border border-amber-500/40 bg-amber-500/10 px-3 py-2.5 text-[13px] text-amber-200" data-testid="cv-assist-warning">
              <AlertTriangle size={15} className="mt-0.5 shrink-0" />
              Sprawdź treść. W CV powinny znaleźć się wyłącznie informacje zgodne z Twoim rzeczywistym doświadczeniem.
            </div>
            <div data-testid="cv-assist-proposal-text" className="whitespace-pre-wrap rounded-card border border-brand-line bg-brand-surface p-4 text-[15px] leading-relaxed text-brand-text">
              {proposal}
            </div>

            <div className="flex flex-wrap gap-2">
              {REFINE_ACTIONS.map(({ key, label, Icon }) => (
                <button
                  key={key}
                  type="button"
                  data-testid={`cv-assist-action-${key}`}
                  onClick={() => generate(key, proposal)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-brand-line px-3.5 py-2 text-[13px] font-medium text-brand-sec transition-colors hover:border-brand-accent/60 hover:text-brand-text"
                >
                  <Icon size={14} /> {label}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-brand-line pt-4">
              <button type="button" data-testid="cv-assist-cancel" onClick={handleClose} className="rounded-button border border-brand-line px-4 py-2.5 text-sm font-semibold text-brand-text hover:border-brand-accent/60">
                Anuluj
              </button>
              <button
                type="button"
                data-testid="cv-assist-insert"
                onClick={handleInsert}
                className="inline-flex items-center gap-2 rounded-button bg-brand-accent px-5 py-2.5 text-sm font-semibold text-brand-on transition-transform hover:-translate-y-0.5"
              >
                <Check size={15} /> Wstaw do CV
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
